/**
 * Mirrors: api/internal/httpapi/inventory.go
 *
 *   GET /inventory              per-kind roll-up
 *   GET /inventory/state        the derived state summary
 *   GET /inventory/:kind        filtered, sorted and PAGED IN SQL
 *   GET /inventory/:kind/:id    one object, with its full attributes
 *
 * `?sort=` accepts only httpapi.inventorySortColumns; anything else is a 400,
 * so the DataTable's sortable columns are exactly this list.
 */
import { coerceArray, get } from "@/lib/api";
import { withQuery } from "./params";
import type { ObjectKind, Page, PageParams, SortDirection } from "./types";
import { tenantPath, tenantScopeKey } from "./tenantScope";

/** httpapi.inventorySortColumns — the whitelist that reaches ORDER BY. */
export const INVENTORY_SORT_COLUMNS = [
  "display_name",
  "object_id",
  "first_seen_at",
  "last_seen_at",
  "deleted_at",
] as const;
export type InventorySortColumn = (typeof INVENTORY_SORT_COLUMNS)[number];

export function isInventorySortColumn(value: string): value is InventorySortColumn {
  return (INVENTORY_SORT_COLUMNS as readonly string[]).includes(value);
}

/** httpapi.parseDeleted. The default hides nothing: a deletion is signal. */
export type DeletedFilter = "any" | "only" | "exclude";

export interface InventoryObject {
  kind: ObjectKind;
  object_id: string;
  display_name: string;
  attributes?: unknown;
  first_seen_run: string;
  last_seen_run: string;
  first_seen_at: string;
  last_seen_at: string;
  /** Non-null is a TOMBSTONE. It travels with its reason and is never dropped. */
  deleted_at: string | null;
  deleted_reason?: string;
  /**
   * The name to show, resolved by the server at read time: the display name,
   * or for an object with none of its own (or a raw one) a name built from
   * what it references — "<role> — <principal>", "<trustee> — Full access on
   * <mailbox>". Absent when nothing resolves.
   */
  label?: string;
  /** userPrincipalName of the user the object is, or names. */
  sign_in_name?: string;
}

export interface KindCount {
  kind: ObjectKind;
  live: number;
  deleted: number;
  total: number;
}

/**
 * httpapi.StateBucket / StateFacet / StateSection / StateSignal.
 *
 * A facet is an EXHAUSTIVE partition: `buckets` always sums to `total`, and the
 * objects whose property carries no value are in an explicit `unknown` or
 * `not_reported` bucket rather than missing. That is what lets the bar show
 * coverage instead of implying it — a facet whose unknown bucket is the whole
 * total is a property nothing measured, not a tenant with none of something.
 */
export interface StateBucket {
  key: string;
  count: number;
}

export interface StateFacet {
  id: string;
  /** The Graph property the partition reads, so an unknown bucket is legible. */
  property: string;
  total: number;
  buckets: StateBucket[];
  /**
   * The facet covers a SUBSET of the kind — the directory-synced accounts
   * rather than all of them — so its total is smaller than the section's on
   * purpose. The screen has to say so, or the reader takes the smaller number
   * for the whole population.
   */
  partial?: boolean;
}

export interface StateSection {
  kind: ObjectKind;
  total: number;
  facets: StateFacet[];
}

/** A cross-cutting subset, with the population it was counted out of. */
export interface StateSignal {
  id: string;
  count: number;
  of: number;
}

export interface InventoryState {
  /** The stale_object_days policy threshold this summary was computed at. */
  stale_days: number;
  cutoff: string;
  sections: StateSection[];
  signals: StateSignal[];
}

export interface InventoryFilter {
  q: string;
  deleted: DeletedFilter;
  sort: InventorySortColumn;
  order: SortDirection;
}

export const defaultInventoryFilter: InventoryFilter = {
  q: "",
  deleted: "any",
  sort: "last_seen_at",
  order: "desc",
};

export const inventoryKeys = {
  all: () => ["inventory", tenantScopeKey()] as const,
  counts: () => ["inventory", tenantScopeKey(), "counts"] as const,
  state: () => ["inventory", tenantScopeKey(), "state"] as const,
  list: (kind: string, filter: InventoryFilter, page: PageParams) =>
    ["inventory", tenantScopeKey(), "list", kind, filter, page.page, page.size] as const,
  detail: (kind: string, id: string) =>
    ["inventory", tenantScopeKey(), "detail", kind, id] as const,
};

export async function fetchInventoryCounts(): Promise<KindCount[]> {
  const raw = await get<{ items: KindCount[] | null }>(tenantPath("/inventory"));
  return coerceArray(raw.items);
}

export async function fetchInventoryState(): Promise<InventoryState> {
  const raw = await get<InventoryState>(tenantPath("/inventory/state"));
  return {
    ...raw,
    sections: coerceArray(raw.sections).map((s) => ({
      ...s,
      facets: coerceArray(s.facets).map((f) => ({ ...f, buckets: coerceArray(f.buckets) })),
    })),
    signals: coerceArray(raw.signals),
  };
}

export async function fetchInventory(
  kind: string,
  filter: InventoryFilter,
  page: PageParams,
): Promise<Page<InventoryObject>> {
  const raw = await get<Page<InventoryObject>>(
    withQuery(tenantPath(`/inventory/${encodeURIComponent(kind)}`), {
      q: filter.q,
      deleted: filter.deleted,
      sort: filter.sort,
      order: filter.order,
      page: page.page,
      size: page.size,
    }),
  );
  return {
    ...raw,
    items: coerceArray(raw.items).map((o) => ({ ...o, deleted_at: o.deleted_at ?? null })),
  };
}

export async function fetchInventoryObject(
  kind: string,
  objectId: string,
): Promise<InventoryObject> {
  const raw = await get<InventoryObject>(
    tenantPath(`/inventory/${encodeURIComponent(kind)}/${encodeURIComponent(objectId)}`),
  );
  return { ...raw, deleted_at: raw.deleted_at ?? null };
}

/**
 * httpapi.ObservedEndpointAgentPairDTO — GET /ai-bp/endpoint-agent-pairs.
 * One distinct "vendor|process" pair from Defender for Endpoint's own local
 * AI agent discovery, as the endpoint_agent_allowlist consumer compares it.
 */
export interface ObservedEndpointAgentPair {
  vendor: string;
  process: string;
  value: string;
  agents: number;
}

export interface ObservedEndpointAgentPairs {
  items: ObservedEndpointAgentPair[];
  /** Live profiles without a vendor or a process: they cannot be listed. */
  unpaired: number;
  agents: number;
}

export const observedPairsKey = () =>
  ["inventory", tenantScopeKey(), "ai-bp", "endpoint-agent-pairs"] as const;

export async function fetchObservedEndpointAgentPairs(): Promise<ObservedEndpointAgentPairs> {
  const raw = await get<ObservedEndpointAgentPairs>(tenantPath("/ai-bp/endpoint-agent-pairs"));
  return { ...raw, items: coerceArray(raw.items) };
}
